// Actions are payloads of informations that are used to modify the
// application's Redux state. They may also act as signals and information
// payloads to sagas when asynchronous operations need to occur. This app uses
// [Flux Standard Actions](https://github.com/acdlite/flux-standard-action) to
// make sure actions have a uniform schema.
//
// http://redux.js.org/docs/basics/Actions.html
import { createAction } from 'redux-actions';
import snakeCase from 'lodash/snakeCase';
import { upperSnakeCase } from '../utils';

// Uper snake case names
function normalize(...names) {
  return names.map(upperSnakeCase);
}

// Create load action type variants with get, loading and loading prefixed
function load(resourceName) {
  return [
    `get ${resourceName}`,
    `loading ${resourceName}`,
    `loaded ${resourceName}`,
  ];
}

// Create multiple load action types (get, loading and loading)
function loadAll(...names) {
  return [].concat(...names.map(load));
}

// Create load action type variants with do, doing and done prefixed
function doit(resourceName) {
  return [
    `do ${resourceName}`,
    `doing ${resourceName}`,
    `done ${resourceName}`,
  ];
}

// Create multiple load action types (do, doing and done)
function doAll(...names) {
  return [].concat(...names.map(doit));
}

// The action type names for this app's Redux/Flux actions. Used to generate
// `ActionTypes` and `Actions` used throughout the app.
// actionName -> ActionType ('action name' -> 'ACTION_NAME')
// actionName -> ActionCreator ('action name' -> 'actionName')
const actionNames = normalize(
  // Data load resource actions
  ...loadAll(),
  ...doAll(),
  'set_preferred_language',
  'set_theme',
  'set_contact_search_config',
  'reset_contact_search_config',
  'set_stickers',
  'set_recent_emojis',
  'set_emoji_skin_tone'
);

// The app's redux action types. Each key is the same as the string action type.
// For each action name, the type is generated by upper snake-casing the phrase.
// assert.equal(ActionTypes.EAT_CAKE, 'EAT_CAKE');
export const ActionTypes = actionNames.reduce((actionTypes, actionName) => {
  const newActionTypes = actionTypes;
  newActionTypes[actionName] = actionName;
  return newActionTypes;
}, {});

// Action creators for the app (functions that return Redux/Flux actions).
// let action = Actions.wingardiumLeviosa('ferret');
// assert.deepEqual(action, { type: 'WINGARDIUM_LEVIOSA', payload: 'ferret' });
export const Actions = actionNames.reduce((actions, actionName) => {
  const newActions = actions;
  const actionCreatorName = snakeCase(actionName);
  newActions[actionCreatorName] = createAction(
    actionName,
    (x) => x,
    (x, y) => y
  );
  return newActions;
}, {});
